/**
 * Promoted from `src/audio/lab/types.ts` (PR13-D). These are the pure,
 * palette-agnostic synthesis primitives every audio consumer needs -
 * production's `AudioManager`/`src/audio/catalog/` and the QA-only
 * `src/audio/lab/` both build on this module. Nothing here knows about
 * palettes, candidates, or QA concepts - that split is what lets
 * production import this file while never importing anything under
 * `src/audio/lab/`.
 */

export type OscillatorShape = "sine" | "triangle" | "square" | "sawtooth";

/** One tone layer: a single oscillator with its own frequency (glide) and shape. A recipe may combine several (e.g. a fundamental plus a fifth for warmth). */
export interface ToneLayer {
  shape: OscillatorShape;
  /** Starting frequency in Hz. */
  startFreqHz: number;
  /** Ending frequency in Hz - equal to `startFreqHz` for a flat tone, lower for a "descending" character, higher for a "rising" one. */
  endFreqHz: number;
  /** Relative mix level for this layer, 0-1 - layers are summed then the recipe's overall `envelope.peakGain` is applied, so this only balances layers against each other. */
  mix: number;
}

/** An optional filtered-noise layer - built from one shared reusable white-noise `AudioBuffer` (see `AudioBackend.ts`), never a new noise generation per play. */
export interface NoiseLayer {
  filterType: "lowpass" | "highpass" | "bandpass";
  filterFreqHz: number;
  filterQ: number;
  /** Relative mix level, 0-1, summed alongside any tone layers before the envelope is applied. */
  mix: number;
}

/** A single tap of a short, non-feedback "shimmer" delay used for rare-event spatial depth - never a full convolution reverb. */
export interface DelayTap {
  delayMs: number;
  /** Gain multiplier relative to the dry signal, 0-1 - always attenuating, never boosting past the dry signal. */
  gain: number;
}

/** A simple attack/decay/sustain/release gain envelope, all times in ms and levels linear (0-1). */
export interface GainEnvelope {
  attackMs: number;
  decayMs: number;
  sustainLevel: number;
  sustainMs: number;
  releaseMs: number;
  /** Peak linear gain at the top of the attack, before the master gain is applied. */
  peakGain: number;
}

/**
 * The pure synthesis description of one sound - plain data, no Web Audio
 * nodes. `AudioBackend.playRecipe()` is the only thing that turns this
 * into actual oscillators/filters/gain nodes.
 */
export interface SynthLayer {
  readonly durationMs: number;
  readonly tones: readonly ToneLayer[];
  readonly noise?: NoiseLayer;
  readonly envelope: GainEnvelope;
  /** Stereo pan, -1 (full left) to 1 (full right) - restrained by construction, never used for anything but a short, tasteful movement on rare events. */
  readonly pan: number;
  /** Optional shimmer/echo taps - only rare/spatial events carry these. */
  readonly delayTaps?: readonly DelayTap[];
  /** True for a sustained-loop variant - reserved for the handful of events treated as a short loop sample rather than a one-shot, looped by re-triggering rather than an unbounded `loop: true` AudioBufferSourceNode. */
  readonly isLoopSample?: boolean;
  /** Approximate normalized perceptual peak loudness, 0-1 - the value cross-recipe fairness tests compare. Never read by `AudioBackend` itself. */
  readonly perceptualLoudness: number;
}

/** The palette-neutral voice a recipe is synthesized through - timbre parameters (waveform, filter character, harmonic color, ...) shared across every event that voice builds. */
export interface Voice {
  readonly tonicHz: number;
  readonly everydayShape: OscillatorShape;
  readonly richShape: OscillatorShape;
  readonly percussiveShape: OscillatorShape;
  readonly noiseFilterType: NoiseLayer["filterType"];
  readonly noiseBrightness: number;
  readonly noiseQ: number;
  readonly attackScale: number;
  readonly releaseScale: number;
  readonly harmonicDetuneSemitones: number;
  readonly shimmerTaps: readonly DelayTap[];
  readonly panWidth: number;
  readonly loudnessTrim: number;
  readonly roughnessNoiseBoost: number;
}

/**
 * Every sound the real game (and its QA lab) needs a recipe for. Adding
 * an id here is the one place both production's catalog and the QA lab's
 * completeness checks require it to be filled in.
 */
export const SOUND_EVENT_IDS = [
  // Everyday actions
  "safe",
  "rush1",
  "rush2",
  "rush3",
  "rush4",
  "rush5",
  "buttonClick",
  "pause",
  "resume",
  // CHAOS
  "chaosAnticipation",
  "jackpot",
  "shrink",
  "legacyScoreHit",
  // WORMHOLE family (wormholeActivation is also CHAOS's "WORMHOLE activation" outcome - one event, one recipe)
  "wormholeActivation",
  "wormholeLoop",
  "wormholeRefresh",
  "wormholeWrap",
  "wormholeWarningPulse",
  "wormholeExpiry",
  "wormholeBite",
  "wormholeClutch",
  // SPEED TRIAL family (speedTrialStart is also CHAOS's "SPEED TRIAL start" outcome)
  "speedTrialStart",
  "speedTrialLoop",
  "speedTrialWarningPulse",
  "speedTrialSuccess",
  "speedTrialExit",
  // STARFRUIT and gates
  "starfruitAppear",
  "starfruitEaten",
  "energyTravel",
  "gateTempOpen",
  "starfruitGateWarning",
  "gateTempClose",
  "gatePermanentOpen",
  "gateEntry",
  // Skill and result events
  "maneuver",
  "doubleDanger",
  "flowIncrease",
  "trickBite",
  "clutchBite",
  "death",
  "levelComplete",
  "starFill",
  "newStarRecord",
  "newHighScore",
] as const;

export type SoundEventId = (typeof SOUND_EVENT_IDS)[number];

/** One scheduled sound within a multi-part timeline - a single overlapping layer of a compact moment (JACKPOT, DEATH) or one distinct beat of a longer story (STARFRUIT/WORMHOLE/SPEED TRIAL) are the same shape, so one scheduler and one safety validator serve every case. */
export interface CandidateLayer {
  readonly atMs: number;
  readonly synth: SynthLayer;
  readonly label: string;
  readonly transposeSemitones?: number;
  /** True for a labeled gap standing in for compressed real elapsed time - no sound plays, only the label is informational. */
  readonly silent?: boolean;
}

/** Total time a layer timeline spans, from its first layer to its last layer's own end. */
export function layersTotalDurationMs(layers: readonly CandidateLayer[]): number {
  return layers.reduce((max, layer) => Math.max(max, layer.atMs + layer.synth.durationMs), 0);
}

/** The loudest single layer in a timeline - the proxy fairness checks compare across sibling candidates in the same family. */
export function layersPeakLoudness(layers: readonly CandidateLayer[]): number {
  return layers.reduce((max, layer) => Math.max(max, layer.synth.perceptualLoudness), 0);
}

/**
 * Lays out a list of layer groups strictly one after another - each
 * segment starts only once the previous segment's own last layer has
 * fully finished playing, plus `gapAfterMs` of silence. This is what
 * guarantees one section's tail/loop can never bleed into the next.
 */
export function concatTimelines(segments: ReadonlyArray<{ layers: readonly CandidateLayer[]; gapAfterMs?: number }>): CandidateLayer[] {
  const result: CandidateLayer[] = [];
  let cursor = 0;
  for (const segment of segments) {
    for (const l of segment.layers) {
      result.push({ ...l, atMs: l.atMs + cursor });
    }
    const segmentEndMs = segment.layers.reduce((max, l) => Math.max(max, l.atMs + l.synth.durationMs), 0);
    cursor += segmentEndMs + (segment.gapAfterMs ?? 300);
  }
  return result;
}

/** Anything with an id and a layer timeline - `Candidate` (QA lab) and a plain `{id, layers}` wrapper around a catalog constant (production) both satisfy this structurally. */
export interface LabeledLayerSource {
  readonly id: string;
  readonly layers: readonly CandidateLayer[];
}

/** Finds the one layer in `source.layers` with the given `label`, throwing immediately if not found - a typo or a renamed label fails loudly instead of silently resolving to `undefined`. Re-zeroes `atMs` to 0 so the extracted layer plays immediately when used standalone. */
export function requireLayerByLabel(source: LabeledLayerSource, label: string): CandidateLayer {
  const found = source.layers.find((l) => l.label === label);
  if (!found) throw new Error(`requireLayerByLabel: ${source.id} has no layer labeled "${label}"`);
  return { ...found, atMs: 0 };
}

/** Same as `requireLayerByLabel`, but for a named GROUP of layers that together form one composite moment (e.g. WORMHOLE's two-part wrap: "pulled" + "emerges") - re-zeroes the whole group relative to its own first layer, preserving their relative spacing. */
export function requireLayersByLabels(source: LabeledLayerSource, labels: readonly string[]): CandidateLayer[] {
  const matches = labels.map((label) => {
    const found = source.layers.find((l) => l.label === label);
    if (!found) throw new Error(`requireLayersByLabels: ${source.id} has no layer labeled "${label}"`);
    return found;
  });
  const baseAtMs = matches[0].atMs;
  return matches.map((l) => ({ ...l, atMs: l.atMs - baseAtMs }));
}
